<script>
import Sortable from 'sortablejs'
import liveblogTimelineItem from './liveblogTimelineItem.vue'

export default {
    name: 'LiveblogTimeline',
    components: { liveblogTimelineItem },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        busy: {
            type: Boolean,
            default: false,
        },
        reorderBusy: {
            type: Boolean,
            default: false,
        },
        canReorder: {
            type: Boolean,
            default: false,
        },
        filter: {
            type: String,
            default: '',
        },
        activeEventId: {
            type: String,
            default: '',
        },
        orderDirection: {
            type: String,
            default: 'DESC',
        },
        eventTypes: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            sortableInstance: null,
        }
    },
    computed: {
        filteredItems() {
            const q = (this.filter || '').trim().toLowerCase()
            if (!q) return this.items
            return this.items.filter((item) => {
                const haystack = [
                    item.content_type,
                    item.event_minute,
                    item.title,
                    item.body,
                    item.event_type_name,
                    item.event_reason_name,
                    item.period_name,
                    item.player_name,
                    item.related_player_name,
                    item.team_code,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                return haystack.includes(q)
            })
        },
    },
    watch: {
        filteredItems() {
            this.scheduleSortableInit()
        },
        canReorder() {
            this.scheduleSortableInit()
        },
        busy() {
            this.scheduleSortableInit()
        },
    },
    mounted() {
        this.scheduleSortableInit()
    },
    beforeDestroy() {
        this.destroySortable()
    },
    methods: {
        getPayloadImageUrls(item) {
            if (!item || !item.payload) return []
            if (item.content_type === 'image' && item.payload.url) {
                return [item.payload.url]
            }
            if (item.content_type === 'gallery' && Array.isArray(item.payload.images)) {
                return item.payload.images.map((img) => img && img.url).filter(Boolean)
            }
            if (item.content_type === 'highlight') {
                const url = item.payload.thumbnail || item.payload.url
                return url ? [url] : []
            }
            return []
        },
        /** @deprecated use getPayloadImageUrls — kept for stale HMR/template references */
        getPayloadImageUrl(item) {
            const urls = this.getPayloadImageUrls(item)
            return urls.length ? urls[0] : null
        },
        scheduleSortableInit() {
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.destroySortable()
                    this.initSortable()
                })
            })
        },
        initSortable() {
            if (!this.canReorder || this.busy || this.reorderBusy) return
            const list = this.$refs.timelineList
            if (!list || !list.children.length) return
            this.sortableInstance = Sortable.create(list, {
                handle: '.liveblog-drag-handle',
                animation: 150,
                ghostClass: 'liveblog-sortable-ghost',
                draggable: '.liveblog-timeline-item-wrap',
                disabled: this.reorderBusy,
                onEnd: (evt) => this.onReorderEnd(evt),
            })
        },
        destroySortable() {
            if (this.sortableInstance) {
                this.sortableInstance.destroy()
                this.sortableInstance = null
            }
        },
        onReorderEnd(evt) {
            if (
                evt.oldIndex == null ||
                evt.newIndex == null ||
                evt.oldIndex === evt.newIndex
            ) {
                return
            }
            const moved = this.filteredItems[evt.oldIndex]
            const target = this.filteredItems[evt.newIndex]
            if (!moved || !target) {
                this.$emit('reorder-cancel')
                return
            }
            const parent = evt.from
            const children = parent.children
            if (evt.oldIndex < evt.newIndex) {
                parent.insertBefore(evt.item, children[evt.oldIndex])
            } else {
                parent.insertBefore(evt.item, children[evt.oldIndex + 1])
            }
            this.$emit('reorder', {
                eventId: moved.event_id,
                targetOrder: target.order,
            })
        },
    },
}
</script>

<template>
    <div class="liveblog-timeline">
        <div class="liveblog-timeline__toolbar">
            <h5 class="liveblog-timeline__title mb-0">
                Timeline
                <span class="text-muted fw-normal">({{ filteredItems.length }})</span>
            </h5>
            <span class="text-muted small">Order: {{ orderDirection }}</span>
        </div>

        <p v-if="canReorder" class="text-muted small mb-2">
            <i class="mdi mdi-drag me-1"></i>Drag entries to reorder
        </p>
        <p v-else-if="filter" class="text-muted small mb-2">Clear search to reorder entries</p>

        <div v-if="busy" class="liveblog-timeline__loading text-center py-5">
            <b-spinner variant="primary" />
            <p class="text-muted small mt-2 mb-0">Loading timeline…</p>
        </div>

        <div v-else-if="!filteredItems.length" class="liveblog-timeline__empty text-center py-5">
            <i class="mdi mdi-timeline-clock-outline display-4 text-muted"></i>
            <p class="text-muted mb-0 mt-2">No entries yet. Add the first event on the right.</p>
        </div>

        <div v-else ref="timelineList" class="liveblog-timeline__list">
            <div
                v-for="item in filteredItems"
                :key="item.event_id"
                class="liveblog-timeline-item-wrap"
            >
                <liveblogTimelineItem
                    :item="item"
                    :active="activeEventId === item.event_id"
                    :can-reorder="canReorder"
                    :event-types="eventTypes"
                    :image-urls="getPayloadImageUrls(item)"
                    @edit="$emit('edit', $event)"
                    @delete="$emit('delete', $event)"
                />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.liveblog-timeline {
    height: 100%;
    display: flex;
    flex-direction: column;
}
.liveblog-timeline__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}
.liveblog-timeline__title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #151a2d;
}
.liveblog-timeline__list {
    flex: 1;
    min-height: 0;
}
</style>
